import { observer } from 'mobx-react'
import type { PropsWithChildren } from 'react'
import { Navigate, useLocation } from 'react-router'

import { routeName } from '~/@common/constants'
import { commonRepo } from '~/@common/services/repo.service'

type Props = PropsWithChildren

/**
 * 라우트 단위 권한 가드.
 * - 역할 게이트: `routeName.parent.*` 는 학부모만, `routeName.student.*` 는 학생만 진입 가능.
 *   restriction 에 명시되지 않은 경로라도 다른 역할이 직접 URL 로 들어오는 것을 막는다.
 * - 기능 게이트: BE restrictions 기반 byFeature.isRouteAllowed(pathname) 로 차단된 경로를 막는다.
 * 차단되면 역할별 기본 경로로 리다이렉트한다.
 */
const PermissionGuardRoute = ({ children }: Props) => {
  const { pathname } = useLocation()
  const { permission } = commonRepo

  const defaultRoute = permission.isParent
    ? routeName.parent.defaultPath
    : routeName.student.defaultPath

  const isParentRoute = pathname.startsWith(routeName.parent.defaultPath)
  const isStudentRoute = pathname.startsWith(routeName.student.defaultPath)

  // 역할-라우트 불일치 차단 (예: 학생이 /parent/* 진입, 학부모가 /student/* 진입)
  if (isParentRoute && !permission.isParent) {
    return <Navigate to={defaultRoute} replace />
  }
  if (isStudentRoute && permission.isParent) {
    return <Navigate to={defaultRoute} replace />
  }

  if (!permission.byFeature.isRouteAllowed(pathname)) {
    return <Navigate to={defaultRoute} replace />
  }

  return children
}

export default observer(PermissionGuardRoute)
